/**
 * API ANAHTARLARI PANELİ — iki modda çalışır:
 * - scope="system" (Admin Paneli): platformun ortak anahtarları. Maskeli görünür,
 *   yalnız üzerine yazılır; hiçbir kullanıcıya gösterilmez.
 * - scope="user" (Ayarlar): kullanıcının KENDİ anahtarları (BYOK). Girerse asistan+ses
 *   onun anahtarıyla çalışır; silerse sistem anahtarına döner.
 * Bakiyeler: DataForSEO ($), ElevenLabs (karakter), Firecrawl (kredi) gerçek sorgu;
 * Anthropic bakiye API'si sunmaz (bilgi notu).
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { KeyRound, Loader2, Check, RefreshCw, Trash2, Wallet } from "lucide-react";

type Masked = { value: Record<string, string> | null; source?: string; hasOwn?: boolean };
type Balance = { ok: boolean; label: string; detail?: string };

const PROVIDER_META: Record<string, { title: string; fields: { key: string; label: string; secret: boolean }[] }> = {
  anthropic: { title: "Anthropic (Claude)", fields: [{ key: "apiKey", label: "API Key (sk-ant-…)", secret: true }] },
  dataforseo: { title: "DataForSEO", fields: [{ key: "login", label: "Login (e-posta)", secret: false }, { key: "password", label: "Password", secret: true }] },
  elevenlabs: { title: "ElevenLabs (Ses)", fields: [{ key: "apiKey", label: "API Key (sk_…)", secret: true }, { key: "voiceId", label: "Voice ID (opsiyonel)", secret: false }] },
  firecrawl: { title: "Firecrawl", fields: [{ key: "apiKey", label: "API Key (fc-…)", secret: true }] },
};

// PAKETİM & TOKEN KULLANIMI — kullanıcı kendi bütçesini görür ("token unsuru bildirimi").
// Site başına hizmet seviyesi sabittir (ayda 20 içerik vb.); bütçe = site × $80.
export function MyPlanCard() {
  const { data } = useQuery({
    queryKey: ["me-usage"],
    staleTime: 60_000,
    queryFn: async (): Promise<{ package: { name: string; siteLimit: number; tokenBudgetUsd: number; priceMonthly: number; description: string } | null; domains: number; usage30: number; usageTotal: number }> => {
      const r = await fetch("/api/me/usage");
      return r.ok ? r.json() : { package: null, domains: 0, usage30: 0, usageTotal: 0 };
    },
  });
  if (!data) return null;
  const p = data.package;
  const pct = p ? Math.min(100, Math.round((data.usage30 / p.tokenBudgetUsd) * 100)) : 0;
  const barColor = pct >= 90 ? "#d92d20" : pct >= 70 ? "#d97706" : "#16a34a";
  // Kullanıcıya DOLAR gösterilmez — token birimi (1000 token = 1 birim maliyet)
  const tok = (usd: number) => Math.round(usd * 1000).toLocaleString("tr-TR");
  return (
    <Card style={{ borderColor: "rgba(245,158,11,0.4)" }}><CardContent className="p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <div className="text-sm font-bold flex items-center gap-1.5"><Wallet className="w-4 h-4" style={{ color: "#b54708" }} /> Paketim & Token Kullanımı</div>
        {p && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#fef3c7", color: "#b54708" }}>{p.name}</span>}
      </div>
      {!p ? (
        <p className="text-[12px] text-muted-foreground">Hesabına paket atanmamış — sınırsız kullanım (ekip hesabı). Paketler: site başına ayda 20 içerik, 10 güçlendirme, keşif + GEO takibi içerir.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-[12px] text-muted-foreground">{p.description}</p>
          <div>
            <div className="flex justify-between text-[11px] mb-0.5">
              <span className="text-muted-foreground">Token hakkı (son 30 gün)</span>
              <span className="font-semibold" style={{ color: barColor }}>{tok(data.usage30)} / {tok(p.tokenBudgetUsd)} token</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
            </div>
            {pct >= 90 && <div className="text-[11px] mt-1" style={{ color: "#d92d20" }}>Token hakkının %{pct}'i kullanıldı — pahalı işlemleri (içerik üretimi, keşif) planlayarak kullan ya da paket yükselt.</div>}
          </div>
          <div className="flex justify-between text-[11px]">
            <span className="text-muted-foreground">Kayıtlı site</span>
            <span className="font-semibold">{data.domains} / {p.siteLimit}</span>
          </div>
        </div>
      )}
    </CardContent></Card>
  );
}

// PROFİL KARTI — isim/parola değişikliği + abonelik iptali + kart bağlama (yakında)
export function ProfileCard() {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const save = async () => {
    const body: Record<string, string> = {};
    if (name.trim()) body.name = name.trim();
    if (newPw) { body.currentPassword = curPw; body.newPassword = newPw; }
    if (!Object.keys(body).length) return;
    const r = await fetch("/api/me/profile", { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    toast(r.ok ? { title: "Profil güncellendi" } : { title: "Güncellenemedi", description: d.error, variant: "destructive" });
    if (r.ok) { setName(""); setCurPw(""); setNewPw(""); }
  };
  const cancelSub = async () => {
    if (!window.confirm("Aboneliğin iptal edilecek ve oturumun kapanacak. Emin misin?")) return;
    const r = await fetch("/api/me/cancel-subscription", { method: "POST" });
    if (r.ok) { window.alert("Aboneliğin iptal edildi."); window.location.reload(); }
    else toast({ title: "İptal edilemedi", description: ((await r.json().catch(() => ({}))) as { error?: string }).error, variant: "destructive" });
  };
  const inputCls = "border rounded-lg px-2.5 py-1.5 text-[12px] bg-white text-gray-800 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500";
  return (
    <Card><CardContent className="p-4 space-y-2">
      <div className="text-sm font-bold">Profilim</div>
      <div className="flex flex-wrap gap-2">
        <input className={inputCls} placeholder="Yeni görünen isim" value={name} onChange={(e) => setName(e.target.value)} style={{ width: 180 }} />
        <input className={inputCls} type="password" placeholder="Mevcut parola" value={curPw} onChange={(e) => setCurPw(e.target.value)} style={{ width: 150 }} autoComplete="current-password" />
        <input className={inputCls} type="password" placeholder="Yeni parola" value={newPw} onChange={(e) => setNewPw(e.target.value)} style={{ width: 150 }} autoComplete="new-password" />
        <Button size="sm" className="h-8" onClick={() => void save()} style={{ background: "linear-gradient(135deg,#b54708,#f79009)" }}>Kaydet</Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center pt-1">
        <Button size="sm" variant="outline" className="h-8 text-[12px]" disabled title="Faturalandırma entegrasyonu yakında">💳 Kart Bağla (yakında)</Button>
        <Button size="sm" variant="outline" className="h-8 text-[12px]" style={{ borderColor: "#f0443866", color: "#f04438" }} onClick={() => void cancelSub()}>Aboneliği İptal Et</Button>
      </div>
    </CardContent></Card>
  );
}

export function ApiKeysPanel({ scope }: { scope: "system" | "user" }) {
  const base = scope === "system" ? "/api/admin/api-keys" : "/api/me/api-keys";
  const balBase = scope === "system" ? "/api/admin/api-balances" : "/api/me/api-balances";
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [edits, setEdits] = useState<Record<string, Record<string, string>>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [showBalances, setShowBalances] = useState(false);

  const { data: keys } = useQuery({
    queryKey: ["api-keys", scope],
    queryFn: async (): Promise<Record<string, Masked>> => { const r = await fetch(base); return r.ok ? r.json() : {}; },
  });
  const { data: balances, isFetching: balLoading, refetch: refetchBal } = useQuery({
    queryKey: ["api-balances", scope],
    enabled: showBalances,
    staleTime: 60_000,
    queryFn: async (): Promise<Record<string, Balance>> => { const r = await fetch(balBase); return r.ok ? r.json() : {}; },
  });

  const save = async (provider: string) => {
    const body = edits[provider];
    if (!body || !Object.values(body).some((v) => v.trim())) return;
    setSaving(provider);
    try {
      const r = await fetch(`${base}/${provider}`, { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      toast(r.ok ? { title: `${PROVIDER_META[provider].title} anahtarı kaydedildi` } : { title: "Kaydedilemedi", description: d.error, variant: "destructive" });
      if (r.ok) setEdits((e) => { const n = { ...e }; delete n[provider]; return n; });
      queryClient.invalidateQueries({ queryKey: ["api-keys", scope] });
      queryClient.invalidateQueries({ queryKey: ["api-balances", scope] });
    } finally { setSaving(null); }
  };

  const removeOwn = async (provider: string) => {
    if (!window.confirm("Kendi anahtarın silinsin mi? (Sistem anahtarına dönülür)")) return;
    await fetch(`${base}/${provider}`, { method: "DELETE" });
    queryClient.invalidateQueries({ queryKey: ["api-keys", scope] });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-bold flex items-center gap-1.5"><KeyRound className="w-4 h-4" style={{ color: "#b54708" }} />
          {scope === "system" ? "Sistem API Anahtarları" : "Kendi API Anahtarların"}
        </div>
        {scope === "system" && (
          <Button size="sm" variant="outline" className="gap-1.5 text-[12px]" onClick={() => { setShowBalances(true); void refetchBal(); }}>
            {balLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />} Bakiyeleri Sorgula
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {scope === "system"
          ? "Bu anahtarlar tüm platformun varsayılanıdır; kullanıcılara hiçbir zaman gösterilmez. Güvenlik gereği mevcut anahtar geri okunamaz (yalnız son 4 hane) — değiştirmek için yenisini yaz."
          : "Kendi Claude (Anthropic) anahtarını girersen asistan ve içerik üretimi SENİN anahtarınla çalışır (maliyet sana yazılır); girmezsen platformun anahtarı kullanılır. Platform anahtarları sana gösterilmez."}
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {/* Kullanıcı BYOK'u bilinçli olarak yalnız Claude ile sınırlı: diğer servislerin
            (DFS/ElevenLabs/Firecrawl) maliyet ve kota yönetimi platformda kalır. */}
        {Object.entries(PROVIDER_META).filter(([provider]) => scope === "system" || provider === "anthropic").map(([provider, meta]) => {
          const cur = keys?.[provider];
          const bal = balances?.[provider];
          return (
            <Card key={provider}><CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-[13px]">{meta.title}</div>
                <div className="flex items-center gap-1.5">
                  {cur?.value && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#dcfce7", color: "#039855" }}>
                    {scope === "system" ? (cur.source === "db" ? "kayıtlı" : "env") : "kendi anahtarın"} {Object.values(cur.value)[0]}
                  </span>}
                  {!cur?.value && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#f4f4f2", color: "#70706c" }}>{scope === "user" ? "sistem anahtarı kullanılıyor" : "tanımsız"}</span>}
                  {scope === "user" && cur?.hasOwn && (
                    <button onClick={() => void removeOwn(provider)} title="Kendi anahtarını sil" className="p-1 rounded hover:bg-muted text-muted-foreground"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
              {showBalances && bal && (
                <div className="text-[11px] rounded-lg px-2 py-1.5" style={bal.ok ? { background: "#f0fdf4", color: "#039855" } : { background: "#fef2f2", color: "#b42318" }}>
                  {bal.label}{bal.detail && <div className="text-[10px] mt-0.5 opacity-80">{bal.detail}</div>}
                </div>
              )}
              {meta.fields.map((f) => (
                <input key={f.key} type={f.secret ? "password" : "text"} placeholder={f.label}
                  value={edits[provider]?.[f.key] ?? ""}
                  onChange={(e) => setEdits((s) => ({ ...s, [provider]: { ...s[provider], [f.key]: e.target.value } }))}
                  className="w-full border rounded-lg px-2.5 py-1.5 text-[12px] bg-white text-gray-800 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" autoComplete="off" />
              ))}
              {edits[provider] && Object.values(edits[provider]).some((v) => v.trim()) && (
                <Button size="sm" className="w-full gap-1 h-7 text-[12px]" onClick={() => void save(provider)} disabled={saving === provider}
                  style={{ background: "linear-gradient(135deg,#b54708,#f79009)" }}>
                  {saving === provider ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Kaydet
                </Button>
              )}
            </CardContent></Card>
          );
        })}
      </div>
    </div>
  );
}
